import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import Avatar from "@/components/base/Avatar";
import ConnectForm from "./components/ConnectForm";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  siteBrand,
  siteFaqs,
  sitePrinciples,
  siteStats,
  siteTimeline,
  siteVoices,
} from "@/mocks/site";

const tones = ["primary", "accent", "secondary"] as const;

export default function AboutPage() {
  const { hash } = useLocation();

  usePageMeta({
    title: "关于我们 | 黑瓜吃料瓜田连线讨论社区",
    description:
      "了解黑瓜吃料：一个围绕泛娱乐话题的中文讨论社区。这里有我们的品牌介绍、网站定位、内容原则、围观口碑，以及投稿爆料与连线方式，欢迎围观群众随时加入讨论。",
    keywords: "黑瓜吃料,关于我们,瓜田讨论区,围观群众,连线吃瓜,内容原则",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑瓜吃料",
        url: "https://geofix.cn/about",
        inLanguage: "zh-CN",
        description:
          "黑瓜吃料的品牌介绍、网站定位、发展理念与内容原则，一个围绕泛娱乐话题的中文讨论社区。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "黑瓜吃料",
        url: "https://geofix.cn/",
        email: "hello@heiguachiliao.com",
        description: "围绕泛娱乐话题的中文讨论社区，聚合围观群众观点，实时跟进热门瓜田话题。",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系黑瓜吃料",
        url: "https://geofix.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: siteFaqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  });

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.replace("#", ""));
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [hash]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <Breadcrumbs items={[{ label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <span className="flex items-center gap-2 text-xs font-medium tracking-wide text-accent-700">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-plant-line text-base" />
                  </span>
                  关于黑瓜吃料
                </span>
                <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                  让每一次围观，都不再是一个人的事
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {siteBrand.intro}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-compass-3-line text-base" />
                    </span>
                    去逛逛话题
                  </Link>
                  <a
                    href="#connect"
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-send-plane-fill text-sm" />
                    </span>
                    投稿连线
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <dl className="grid grid-cols-2 gap-4">
                  {siteStats.map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-card border border-background-200 bg-background-50 p-4"
                    >
                      <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
                        <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                          <i className={`${stat.icon} text-sm`} />
                        </span>
                        {stat.label}
                      </dt>
                      <dd className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                        {stat.value}
                        <span className="ml-0.5 text-sm font-normal text-foreground-500">
                          {stat.suffix}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16">
          <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="h-[280px] w-full overflow-hidden rounded-card border border-background-200 bg-background-200 md:h-[360px]">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20flat%20illustration%20of%20a%20tranquil%20watermelon%20field%20at%20dusk%20with%20soft%20chat%20bubbles%20floating%20above%2C%20coral%20green%20and%20cream%20color%20palette%2C%20minimal%20editorial%20poster%20style%2C%20clean%20layered%20composition&width=1000&height=1100&seq=hg-about-brand-01&orientation=portrait"
                  alt="黑瓜吃料品牌理念插画"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们想做的，其实就是把观点留下
              </h2>
              <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  黑瓜吃料最早只是一个记录追剧感受的小角落。写着写着，我们发现真正有意思的从来不是结论，而是不同的人看到同一件事时，那些完全不一样的判断。
                </p>
                <p>
                  于是我们开始把讨论整理成话题：给出背景、梳理脉络、保留分歧。这样一来，围观不再只是路过，而是能留下点什么。
                </p>
                <p>
                  我们不做结论的裁判，只做一个安静但认真负责的记录者。只要你还愿意聊，这里就一直有位置。
                </p>
              </div>
              <div className="mt-7 flex flex-wrap gap-2">
                {["泛娱乐话题", "观点聚合", "讨论社区", "边吃边聊"].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                内容原则
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                热闹会过去，原则得留下。这四条是我们做内容时的底线，也是讨论区能一直聊下去的原因。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {sitePrinciples.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col rounded-card border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一路走来
            </h2>
            <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {siteTimeline.map((step, index) => (
                <li
                  key={step.id}
                  className="relative rounded-card border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                    {index + 1}
                  </span>
                  <span className="mt-4 block text-xs font-medium tracking-wide text-primary-600">
                    {step.year}
                  </span>
                  <h3 className="mt-1.5 text-base font-bold text-foreground-950">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="voices" className="w-full border-y border-background-200 bg-secondary-50 py-12 md:py-16">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                围观口碑
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                来自讨论区的真实反馈，也是我们继续更新的动力。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {siteVoices.map((voice, index) => (
                <figure
                  key={voice.id}
                  className="flex h-full flex-col rounded-card border border-secondary-200 bg-background-50 p-5"
                >
                  <span className="flex h-8 w-8 items-center justify-center text-secondary-500">
                    <i className="ri-double-quotes-l text-2xl" />
                  </span>
                  <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">
                    {voice.text}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-secondary-100 pt-4">
                    <Avatar name={voice.user} tone={tones[index % tones.length]} size="sm" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground-900">
                        {voice.user}
                      </span>
                      <span className="block truncate text-xs text-foreground-500">{voice.topic}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="w-full py-12 md:py-16">
          <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                常见问题
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                关于内容来源、讨论规则和参与方式，这里有几个最常见的回答。没找到答案的话，往下连线我们就好。
              </p>
            </div>
            <div className="lg:col-span-8">
              <FaqAccordion items={siteFaqs} />
            </div>
          </div>
        </section>

        <section id="connect" className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{siteBrand.contactText}</p>

              <ul className="mt-7 flex flex-col gap-4">
                <li className="flex items-start gap-3 rounded-card border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-xl" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">邮件联系</span>
                    <a
                      href={`mailto:${siteBrand.email}`}
                      rel="nofollow"
                      className="mt-0.5 block text-sm text-foreground-600 transition-colors hover:text-primary-600"
                    >
                      {siteBrand.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-card border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className="ri-time-line text-xl" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">回复时间</span>
                    <span className="mt-0.5 block text-sm text-foreground-600">
                      工作日 10:00 - 18:00，通常 24 小时内回复
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-card border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                    <i className="ri-chat-smile-2-line text-xl" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">讨论区留言</span>
                    <span className="mt-0.5 block text-sm text-foreground-600">
                      任意话题页底部都能直接发表观点
                    </span>
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <ConnectForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}